import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, TextField, Grid, CircularProgress, Autocomplete } from "@mui/material";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { LoadingButton } from '@mui/lab';
import deviceServices from 'app/services/automations/device-services';

const DeviceEditModal = props => {
    const {
        recordID,
        isOpen,
        onClose=()=>{},
        onRecordChange=()=>{},
        onRecordAdd=()=>{},
        defaultStoreId=null,
        defaultStore=null,
        lockStore=false,
        zoneOptions=[],
    } = props;

    const [loadingRecord, setLoadingRecord] = useState(false)
    const [currentRecord, setCurrentRecord] = useState(null)
    const [editOptions, setEditOptions] = useState(null)
    const [saving, setSaving] = useState(false)

    const isValidId = (value) => ![null, undefined, '', 'null', 'undefined'].includes(value)
    const hasRecordId = isValidId(recordID)
    const selectedDeviceTypeId = currentRecord?.device_type_id || currentRecord?.device_type?.id || null
    const selectedDeviceType = _.find(editOptions?.device_types, (dt) => `${dt?.id}` === `${selectedDeviceTypeId}`) || currentRecord?.device_type || null
    const selectedStoreId = currentRecord?.store_id || currentRecord?.store?.id || defaultStoreId || defaultStore?.id || null
    const selectedStore = _.find(editOptions?.stores, (store) => `${store?.id}` === `${selectedStoreId}`) || currentRecord?.store || defaultStore || null

    useEffect(() => {
        if(!isOpen) return

        if(isValidId(recordID)){
            loadRecord(recordID)
        } else {
            setCurrentRecord(null)
            deviceServices.getNewDevice('?include=store,device_type.measurement_types,latest_device_metric').then(resp => {
                const nextStoreId = defaultStoreId || defaultStore?.id || resp?.store_id || resp?.store?.id || null
                setCurrentRecord({
                    ...resp,
                    store: defaultStore || resp?.store || null,
                    store_id: nextStoreId,
                })
            });
        }

        deviceServices.getEditOptions(isValidId(recordID) ? recordID : null).then(resp => { setEditOptions(resp) });
    }, [recordID, isOpen, defaultStoreId, defaultStore])

    const loadRecord = (id) => {
        if(!id) return
        setLoadingRecord(true)
        setCurrentRecord(null)
        deviceServices.getDevice(id, '?include=store,device_type.measurement_types,latest_device_metric')
            .then(resp => {
                const nextStoreId = resp?.store_id || resp?.store?.id || null
                setCurrentRecord({
                    ...resp,
                    store: resp?.store || null,
                    store_id: nextStoreId,
                })
                setLoadingRecord(false)
            })
            .catch(error => {
                setLoadingRecord(false)
            });
    }

    const saveRecord = () => {
        if(!currentRecord) return

        setSaving(true)
        const hasPersistedId = isValidId(currentRecord?.id)
        const basePayload = {
            name: currentRecord?.name || '',
            kind: currentRecord?.kind || '',
            zone: currentRecord?.zone || '',
            store_id: lockStore
                ? (defaultStoreId || defaultStore?.id || currentRecord?.store_id || currentRecord?.store?.id || null)
                : (currentRecord?.store_id || currentRecord?.store?.id || defaultStoreId || null),
        }

        const promise = hasRecordId ?
            deviceServices.updateDevice(currentRecord.id || recordID, '?include=store,device_type.measurement_types,latest_device_metric', { device: basePayload })
        :
            deviceServices.createDevice('?include=store,device_type.measurement_types,latest_device_metric', {
                device: {
                    ...basePayload,
                    code: currentRecord?.code || '',
                    device_type_id: selectedDeviceTypeId,
                }
            })

        promise.then(resp => {
            if(hasRecordId && hasPersistedId){
                onRecordChange(resp)
            } else {
                onRecordAdd(resp)
            }
            onClose()
            setSaving(false)
        }).catch(function (error) {
            setSaving(false)
        });
    }

    const modalTitle = () => {
        if(currentRecord && currentRecord.id) {
            return currentRecord.name
        } else {
            return loadingRecord ? 'Cargando' : 'Nuevo dispositivo'
        }
    }

    const getStoreLabel = (store) => {
        if (typeof store === "string") return store;
        return store?.name || "";
    };

    const getDeviceTypeLabel = (deviceType) => {
        if(!deviceType) return '';
        const brand = deviceType?.brand || '';
        const model = deviceType?.model || '';
        const code = deviceType?.code || '';
        return _.compact([brand, model, code ? `(${code})` : '']).join(' ');
    };

    const normalizeAutocompleteText = (value) => {
        if(typeof value === 'string') return value;
        if(value && typeof value === 'object'){
            if(typeof value.inputValue === 'string') return value.inputValue;
            if(typeof value.label === 'string') return value.label;
        }
        return '';
    };

    const availableZoneOptions = _.uniq(
        _.compact([
            ...(_.isArray(editOptions?.zones) ? editOptions.zones : []),
            ...(_.isArray(zoneOptions) ? zoneOptions : []),
        ])
    ).sort((a, b) => a.localeCompare(b));

    return (
        <React.Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={"sm"}
                open={isOpen}
                onClose={onClose}>
                <DialogTitle>
                    <Typography variant={"h2"} color="primary">{modalTitle()}</Typography>
                </DialogTitle>
                <DialogContent>
                    { loadingRecord ?
                        <Div sx={{ textAlign: 'center' }}>
                            <CircularProgress />
                        </Div>
                    : 
                        <React.Fragment>
                            <Grid container spacing={3} alignItems={"baseline"} sx={{pt: 1, mb: 3}}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Código"
                                        value={currentRecord?.code || ''}
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Tipo de sensor"
                                        value={getDeviceTypeLabel(selectedDeviceType)}
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        label="Nombre"
                                        error={!currentRecord?.name}
                                        value={currentRecord?.name || ''}
                                        onChange={(e) => setCurrentRecord({...currentRecord, name: e.target.value})}
                                    />
                                </Grid>
                                {!lockStore &&
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            id="device-edit-store-id"
                                            options={editOptions?.stores || []}
                                            value={selectedStore}
                                            getOptionLabel={getStoreLabel}
                                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                            renderInput={(params) => <TextField {...params} label="Local" />}
                                            onChange={(e, v) => setCurrentRecord({...currentRecord, store: v, store_id: v?.id || null})}
                                        />
                                    </Grid>
                                }
                                <Grid item xs={12}>
                                    <Autocomplete
                                        id="device-edit-kind"
                                        freeSolo
                                        options={editOptions?.kinds || []}
                                        value={currentRecord?.kind || ''}
                                        renderInput={(params) => <TextField {...params} label="Tipo de dispositivo" />}
                                        onChange={(e, v) => setCurrentRecord({...currentRecord, kind: v})}
                                        onInputChange={(e, v) => setCurrentRecord({...currentRecord, kind: v})}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        id="device-edit-zone"
                                        freeSolo
                                        options={availableZoneOptions}
                                        selectOnFocus
                                        clearOnBlur
                                        handleHomeEndKeys
                                        value={currentRecord?.zone || ''}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Zona"
                                                helperText="Selecciona una zona existente o escribe una nueva."
                                            />
                                        )}
                                        onChange={(e, v) => setCurrentRecord({...currentRecord, zone: normalizeAutocompleteText(v)})}
                                        onInputChange={(e, v) => setCurrentRecord({...currentRecord, zone: v})}
                                    />
                                </Grid>
                            </Grid>
                            {/* <Typography variant={"h4"} color="primary">Datos generales</Typography>
                            <Grid container spacing={3} alignItems={"baseline"} sx={{pt: 1, mb: 3}}>
                                <Grid item xs={6} lg={4}>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        label="Nombre"
                                        error={!currentRecord?.first_name}
                                        value={currentRecord?.first_name || ''}
                                        onChange={(e) => setCurrentRecord({...currentRecord, first_name: e.target.value})}
                                    />
                                </Grid>
                                <Grid item xs={6} lg={4}>
                                    <TextField
                                        fullWidth
                                        label="Apellido"
                                        error={!currentRecord?.last_name}
                                        value={currentRecord?.last_name || ''}
                                        onChange={(e) => setCurrentRecord({...currentRecord, last_name: e.target.value})}
                                    />
                                </Grid>
                                <Grid item xs={6} lg={4}>
                                    <TextField
                                        fullWidth
                                        label="Correo electrónico"
                                        error={!currentRecord?.email}
                                        value={currentRecord?.email || ''}
                                        onChange={(e) => setCurrentRecord({...currentRecord, email: e.target.value})}
                                    />
                                </Grid>
                                <Grid item xs={6} lg={4}>
                                    <PrettyPatternInput
                                        kind="phone"
                                        fullWidth
                                        label="Teléfono primario"
                                        helperText="Ej: 56 9 1234 5678"
                                        error={currentRecord.phone_primary && !validatePhone(currentRecord?.phone_primary)}
                                        value={currentRecord?.phone_primary || ''}
                                        onValueChange={(vals, sourceInfo) => { setCurrentRecord({...currentRecord, phone_primary: vals.value}) }}
                                    />
                                </Grid>
                                <Grid item xs={6} lg={4}>
                                    <PrettyPatternInput
                                        kind="phone"
                                        fullWidth
                                        label="Teléfono secundario"
                                        helperText="Ej: 56 9 1234 5678"
                                        error={currentRecord.phone_secondary && !validatePhone(currentRecord?.phone_secondary)}
                                        value={currentRecord?.phone_secondary || ''}
                                        onValueChange={(vals, sourceInfo) => { setCurrentRecord({...currentRecord, phone_secondary: vals.value}) }}
                                    />
                                </Grid>
                            </Grid>

                            <Typography variant={"h4"} color="primary">Perfiles</Typography>
                            <Grid container spacing={3} alignItems={"baseline"} sx={{pt: 1, mb: 3}}>
                                { editOptions?.profile_names &&
                                    <Grid item xs={6} lg={4}>
                                        <FormGroup>
                                            {_.map(editOptions.profile_names, (p, i) => {
                                                return (
                                                    <FormControlLabel label={p.human_name} control={
                                                        <Checkbox color="success" checked={currentRecord?.profiles && _.filter(currentRecord?.profiles, { name: p.name } ).length > 0} icon={<CircleOutlinedIcon/>} checkedIcon={<CheckCircleIcon/>} onChange={(e) => handleProfileSelect(e, p.name)}/>
                                                    } />
                                                )
                                            })}
                                        </FormGroup>
                                    </Grid>
                                }
                            </Grid> */}
                        </React.Fragment>
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { onClose() } }>Cerrar</Button>
                    <LoadingButton
                        loading={saving}
                        variant="contained"
                        color="success"
                        size="small"
                        disabled={
                            !currentRecord?.name ||
                            (!hasRecordId && !selectedDeviceTypeId) ||
                            !(currentRecord?.store_id || currentRecord?.store?.id || defaultStoreId || defaultStore?.id)
                        }
                        endIcon={<SaveOutlinedIcon/>}
                        loadingPosition="end"
                        onClick={saveRecord}>
                            { saving ? 'Guardando' : 'Guardar' }
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}

export default DeviceEditModal;
